import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import ProcessMapModal from './ProcessMapModal';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  Plus, 
  Link2, 
  Unlink,
  Map,
  Eye,
  Edit,
  Loader2,
  ExternalLink,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const LinkedProcessMaps = ({ 
  entityType, // 'idea' or 'project'
  entityId,
  entityName,
  readOnly = false
}) => {
  const navigate = useNavigate();
  const [linkedMaps, setLinkedMaps] = useState([]);
  const [allMaps, setAllMaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedMapToLink, setSelectedMapToLink] = useState('');
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    if (entityId) {
      fetchLinkedMaps();
    }
  }, [entityId]);

  const fetchLinkedMaps = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/process-maps/by-entity/${entityType}/${entityId}`);
      setLinkedMaps(response.data);
    } catch (error) {
      console.error('Failed to fetch linked process maps:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllMaps = async () => {
    try {
      const response = await api.get('/process-maps');
      // Filter out already linked maps
      const linkedIds = linkedMaps.map(m => m.id);
      setAllMaps(response.data.filter(m => !linkedIds.includes(m.id)));
    } catch (error) {
      console.error('Failed to fetch process maps:', error);
    }
  };

  const handleOpenLinkDialog = () => {
    fetchAllMaps();
    setShowLinkDialog(true);
    setSelectedMapToLink('');
  };

  const handleLinkExisting = async () => {
    if (!selectedMapToLink) return;
    
    setLinking(true);
    try {
      await api.post(`/process-maps/${selectedMapToLink}/link?entity_type=${entityType}&entity_id=${entityId}`);
      toast.success('Process map linked');
      setShowLinkDialog(false);
      fetchLinkedMaps();
    } catch (error) {
      console.error('Failed to link process map:', error);
      toast.error('Failed to link process map');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = async (mapId, mapName) => {
    if (!window.confirm(`Unlink "${mapName}" from this ${entityType}?`)) return;
    
    try {
      await api.post(`/process-maps/${mapId}/unlink?entity_type=${entityType}&entity_id=${entityId}`);
      toast.success('Process map unlinked');
      fetchLinkedMaps();
    } catch (error) {
      console.error('Failed to unlink process map:', error);
      toast.error('Failed to unlink process map');
    }
  };

  const handleNewMapCreated = (newMap) => {
    setLinkedMaps(prev => [...prev, newMap]);
    setShowCreateModal(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Published': return 'bg-emerald-100 text-emerald-700';
      case 'Draft': return 'bg-slate-100 text-slate-700';
      case 'Archived': return 'bg-amber-100 text-amber-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Map className="w-4 h-4 text-[#235894]" />
              Process Maps
              {linkedMaps.length > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {linkedMaps.length}
                </Badge>
              )}
            </CardTitle>
            {!readOnly && (
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleOpenLinkDialog}
                >
                  <Link2 className="w-4 h-4 mr-1" />
                  Link Existing
                </Button>
                <Button 
                  size="sm"
                  onClick={() => setShowCreateModal(true)}
                  className="bg-[#235894] hover:bg-[#1E4B80]"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Create New
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {linkedMaps.length === 0 ? (
            <div className="text-center py-6 border-2 border-dashed rounded-lg">
              <Map className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500 mb-3">
                No process maps linked yet
              </p>
              {!readOnly && (
                <div className="flex gap-2 justify-center">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleOpenLinkDialog}
                  >
                    <Link2 className="w-4 h-4 mr-1" />
                    Link Existing
                  </Button>
                  <Button 
                    size="sm"
                    onClick={() => setShowCreateModal(true)}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Create New
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {linkedMaps.map((map) => (
                <div 
                  key={map.id}
                  className="flex items-center justify-between p-3 rounded-lg border bg-slate-50 hover:bg-slate-100 transition-colors group"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <Map className="w-5 h-5 text-[#235894] flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 truncate">{map.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge className={`${getStatusColor(map.status)} text-xs`}>
                          {map.status}
                        </Badge>
                        <span className="text-xs text-slate-500">v{map.version}</span>
                        {map.department && (
                          <span className="text-xs text-slate-400">• {map.department}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => navigate(`/process-maps/${map.id}`)}
                      title="View"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => navigate(`/process-maps/${map.id}/edit`)}
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    {!readOnly && (
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleUnlink(map.id, map.name)}
                        title="Unlink"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Unlink className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Link Existing Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link Existing Process Map</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-2">
                Select Process Map
              </label>
              <Select
                value={selectedMapToLink}
                onValueChange={setSelectedMapToLink}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a process map..." />
                </SelectTrigger>
                <SelectContent>
                  {allMaps.length === 0 ? (
                    <div className="p-4 text-center text-sm text-slate-500">
                      No available process maps to link
                    </div>
                  ) : (
                    allMaps.map((map) => (
                      <SelectItem key={map.id} value={map.id}>
                        <div className="flex items-center gap-2">
                          <span>{map.name}</span>
                          <Badge className={`${getStatusColor(map.status)} text-xs`}>
                            {map.status}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowLinkDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleLinkExisting}
                disabled={!selectedMapToLink || linking}
                className="bg-[#235894] hover:bg-[#1E4B80]"
              >
                {linking ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Link2 className="w-4 h-4 mr-2" />
                )}
                Link Map
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create New Modal */}
      <ProcessMapModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={handleNewMapCreated}
        entityType={entityType}
        entityId={entityId}
        entityName={entityName}
      />
    </>
  );
};

export default LinkedProcessMaps;
